import { useEffect, useState } from "react";
import Select from "react-select";
import Commissions from "./Commissions";
import { useData, useDispatch } from "../../store/DataProvider";
import {
    handleApiData,
    logoutAdmin,
    initData,
    getAllStaff,
    deleteStaff,
    getMonthlySales,
    unsetAdminAlert,
} from "../../store/actions";

const StaffAdmin = () => {
    const data = useData();
    const dispatch = useDispatch();
    const {
        staffObj,
        staffOptions,
        adminAlert,
        monthlySales,
        commission,
        allStaff,
        shopOptions,
    } = data;

    /* Which list the selector is showing. staffObj and staffOptions are the
       active-only pair that also feeds the POS; allStaff is fetched separately
       and read nowhere else. The "-- New Staff --" option and the preset record
       behind it are taken from the active pair either way, so adding staff
       behaves the same in both. */
    const [showAll, setShowAll] = useState(false);
    const showingAll = showAll && !!allStaff;
    const shownStaffObj = showingAll
        ? { ...allStaff.staffObj, "-1": staffObj["-1"] }
        : staffObj;
    const shownStaffOptions = showingAll
        ? [...allStaff.staffOptions, ...staffOptions.filter(o => o.value === -1)]
        : staffOptions;

    /* Yes/No rather than Active/Inactive: the two sit side by side and differ
       by two letters, which is easy to misread on a field that decides whether
       someone appears on the POS screen at all. */
    const activeOptions = [
        { value: true, label: "Yes" },
        { value: false, label: "No" },
    ];

    useEffect(() => {
        logoutAdmin(dispatch);
    }, [dispatch]);

    useEffect(() => {
        if (staffOptions.length === 0) {
            initData(dispatch);
        }
    }, [staffOptions, dispatch]);

    useEffect(() => {
        if (monthlySales.length === 0) {
            getMonthlySales(null, dispatch);
        }
    }, [monthlySales, dispatch]);

    /* Unsure why cloning (...) necessary here
     * think it has to do with handling undefined async data */
    const { options: monthOptions, selected } = { ...monthlySales.selectObj };

    const [selectedStaffOpt, setSelectedStaffOpt] = useState();
    const [selectedStaffId, setSelectedStaffId] = useState();
    const [selectedStaffName, setSelectedStaffName] = useState();
    const [thai, setThai] = useState();
    const [english, setEnglish] = useState();
    const [username, setUsername] = useState();
    const [commissionRate, setCommissionRate] = useState();
    /* the other two ways a person can be paid. Blank and zero both mean "not
       on this one", so a pure-commission stylist carries no daily rate and a
       salaried one carries no commission -- and someone can have both. */
    const [dailyRate, setDailyRate] = useState();
    const [minimum, setMinimum] = useState();
    /* the shop this person normally works at -- the default a sale of theirs
       will take, overridable on the sale itself */
    const [shopOpt, setShopOpt] = useState(null);
    /* the boolean itself, not an option object: DOM radio values are strings,
       and the stored value is compared with === when a staff member is picked */
    const [active, setActive] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState();
    const [commissionTotals, setCommissionTotals] = useState({});
    const [payments, setPayments] = useState({});

    /* Refused as it is typed, the way every other money field in the app
       does it. These three had no guard: a comma -- and "15,000" is how
       anyone writes it -- made Number() return NaN, which greyed out Save
       with nothing to say why. Worse, onClickSubmit reads `Number(x) || 0`,
       so a typo that got past the check would store a 0 and quietly wipe
       someone's minimum.

       Not the older `!Number(value)` guard used elsewhere, which also
       refuses "0" -- harmless on an expense amount, wrong here, where zero
       is how you say "not on this one". */
    const refuseIfNotMoney = value =>
        Boolean(value) &&
        (!Number.isFinite(Number(value)) || Number(value) < 0);

    const onChangeMoney = setter => value => {
        if (refuseIfNotMoney(value)) {
            return;
        }
        setter(value);
    };

    const formIsValid =
        selectedStaffId &&
        thai &&
        english &&
        username &&
        username.length > 2 &&
        typeof active === "boolean" &&
        shopOpt &&
        Number(commissionRate) >= 0 &&
        Number(commissionRate) <= 100 &&
        commissionRate !== "" &&
        /* blank is fine on both -- it means zero -- but a typo must not be
           read as zero and silently wipe someone's daily rate */
        (dailyRate === "" || Number(dailyRate) >= 0) &&
        (minimum === "" || Number(minimum) >= 0) &&
        !Number.isNaN(Number(dailyRate)) &&
        !Number.isNaN(Number(minimum));

    useEffect(() => {
        setSelectedMonth(selected);
    }, [selected]);

    /* RECONCILE REDUNDANCY WITH onSelectStaff BELOW - set up helper function */
    useEffect(() => {
        if (selectedStaffId) {
            const { totals, columns, columnsByStaff, customStyles, striped, payments } =
                commission;
            const data = totals[selectedStaffId];
            setCommissionTotals({
                data,
                /* this person's columns: a stylist on commission is not
                   shown a salary column of zeros, nor Soom a commission one */
                columns: columnsByStaff?.[selectedStaffId] || columns,
                customStyles,
                striped,
            });
            setPayments(payments[selectedStaffId]);
        } else {
            setCommissionTotals({});
        }
    }, [selectedStaffId, commission]);

    useEffect(() => {
        const { message } = adminAlert;
        if (message) {
            window.alert(message);
            unsetAdminAlert(dispatch);
        }
    }, [adminAlert, dispatch]);

    const onSelectMonth = opt => {
        setSelectedMonth(opt);
        getMonthlySales(opt.value, dispatch);
    };

    const onSelectActive = value => {
        if (!selectedStaffId) {
            return null;
        }
        setActive(value);
    };

    const onSelectStaff = opt => {
        setSelectedStaffOpt(opt);
        const { value, label } = opt;
        setSelectedStaffId(value);
        const thisStaff = shownStaffObj[value];
        /* built from the record rather than taken from the option label, which
           carries the "— inactive" marker: this name is read back in a
           sentence, and "Pay ทา (Ta) — inactive THB 500" is not one */
        setSelectedStaffName(
            thisStaff.english ? `${thisStaff.thai} (${thisStaff.english})` : label,
        );
        setEnglish(thisStaff.english);
        setThai(thisStaff.thai);
        setUsername(thisStaff.username);
        setCommissionRate(thisStaff.commission * 100);
        setDailyRate(thisStaff.dailyRate || 0);
        setMinimum(thisStaff.minimum || 0);
        setShopOpt(
            (shopOptions || []).find(o => o.value === thisStaff.shopId) || null,
        );
        /* a new staff member arrives from getData with active: true already
           set among its presets, so New Staff defaults to Yes */
        setActive(thisStaff.active);

        const { totals, columns, columnsByStaff, customStyles, striped, payments } =
            commission;
        const data = totals[value];
        setCommissionTotals({
            data,
            columns: columnsByStaff?.[value] || columns,
            customStyles,
            striped,
        });
        setPayments(payments[value]);
    };

    const onClickSubmit = () => {
        /* safety measure - shouldn't be possible */
        if (!formIsValid) {
            window.alert("Please make sure all fields are valid.");
        }
        const data = {
            staffId: selectedStaffId,
            thai,
            english,
            username,
            active,
            shopId: shopOpt.value,
            commission: commissionRate === 0 ? 0 : commissionRate / 100,
            dailyRate: Number(dailyRate) || 0,
            minimum: Number(minimum) || 0,
        };
        /* upsertStaff returns the rebuilt all-staff list with its reply, so
           there is nothing to refetch here and nothing to race */
        handleApiData(data, dispatch, "upsertStaff");
        resetFormFields();
        initData(dispatch);
    };

    const resetFormFields = () => {
        setSelectedStaffOpt(null);
        const funcs = [
            setThai,
            setEnglish,
            setUsername,
            setCommissionRate,
            setActive,
            setShopOpt,
            setSelectedStaffId,
        ];
        funcs.forEach(func => func(null));
    };

    /* Only for a record the server marked as never having been used: inactive,
       no sales, no commission payments. Every one of those is checked again on
       the server before anything is deleted, because this flag is only as
       fresh as the last time the list was fetched. */
    const selectedIsOrphan = !!(
        selectedStaffId && shownStaffObj[selectedStaffId]?.orphan
    );

    const onClickDeleteOrphan = () => {
        const { thai, english } = shownStaffObj[selectedStaffId];
        const confirmed = window.confirm(
            `Delete ${thai} (${english})?\n\nThis record has never had a sale ` +
                `or a commission payment against it. Deleting it cannot be undone.`,
        );
        if (!confirmed) {
            return;
        }
        /* deleteStaff returns the rebuilt list with its reply, for the same
           reason -- a refetch here could outrun the delete and put the record
           back on screen, which is what it did */
        deleteStaff(selectedStaffId, dispatch);
        resetFormFields();
        initData(dispatch);
    };

    const onToggleShowAll = () => {
        /* the selection goes either way round: switching lists can leave
           somebody selected who is not in the new one, and the form and the
           commissions panel both read the selected id straight out of it */
        resetFormFields();
        if (!showAll) {
            getAllStaff(dispatch);
        }
        setShowAll(!showAll);
    };

    const onClickReset = () => {
        if (!selectedStaffId) {
            window.alert("Please select staff.");
            return;
        }
        handleApiData({ staffId: selectedStaffId }, dispatch, "resetPassword");
    };

    return (
        <div className="admin-wrapper">
            <div className="admin-column">
                <div className="data-wrapper">
                    <div style={{ marginTop: "20px", fontSize: "22px" }}>
                        Edit / Enter Staff
                    </div>

                    <div className="select-wrapper" style={{ borderRight: 0 }}>
                        <div className="spacer-div">&nbsp;</div>
                        <Select
                            value={selectedStaffOpt}
                            options={shownStaffOptions}
                            onChange={opt => onSelectStaff(opt)}
                            placeholder="Select staff"
                        />
                    </div>

                    {selectedStaffId ? (
                        <div className="admin-staff-container">
                            <div className="admin-staff-row">
                                <div className="admin-input-container">
                                    <div className="admin-staff-label">
                                        Name (Thai):
                                    </div>
                                    <input
                                        className="admin-staff-input"
                                        type="text"
                                        placeholder="Pasah Thai"
                                        value={thai ? thai : ""}
                                        onChange={e => setThai(e.target.value)}
                                        disabled={!selectedStaffId}
                                    />
                                </div>
                                <div className="admin-input-container">
                                    <div className="admin-staff-label">
                                        Name (English)
                                    </div>
                                    <input
                                        className="admin-staff-input"
                                        type="text"
                                        placeholder="English language"
                                        value={english ? english : ""}
                                        onChange={e =>
                                            setEnglish(e.target.value)
                                        }
                                        disabled={!selectedStaffId}
                                    />
                                </div>
                            </div>
                            <div className="admin-staff-row">
                                <div
                                    className="admin-input-container"
                                    style={{
                                        width: "200px",
                                    }}
                                >
                                    <div className="admin-staff-label">
                                        Username:
                                    </div>
                                    <input
                                        className="admin-staff-input"
                                        type="text"
                                        placeholder="Login username"
                                        value={username ? username : ""}
                                        onChange={e =>
                                            setUsername(e.target.value)
                                        }
                                        disabled={!selectedStaffId}
                                    />
                                </div>
                                <div className="admin-input-container admin-input-comm">
                                    <div className="admin-staff-label">
                                        Comm:
                                    </div>
                                    <input
                                        className="admin-staff-input"
                                        type="text"
                                        placeholder="%"
                                        /* support value===0 */
                                        value={
                                            commissionRate?.toString().length >
                                            0
                                                ? commissionRate
                                                : ""
                                        }
                                        onChange={e =>
                                            onChangeMoney(setCommissionRate)(
                                                e.target.value,
                                            )
                                        }
                                        disabled={!selectedStaffId}
                                        style={{
                                            textAlign: "center",
                                        }}
                                    />
                                </div>
                                {/* The three ways a person is paid sit
                                    together, because which of them apply is
                                    the thing being decided: a stylist on
                                    commission has a rate and no daily, a
                                    salaried one the reverse, and the minimum
                                    is a floor under whatever they come to.
                                    Blank reads as zero in all three. */}
                                <div className="admin-input-container admin-input-comm">
                                    <div className="admin-staff-label">
                                        Daily:
                                    </div>
                                    <input
                                        className="admin-staff-input"
                                        type="text"
                                        placeholder="฿"
                                        value={
                                            dailyRate?.toString().length > 0
                                                ? dailyRate
                                                : ""
                                        }
                                        onChange={e =>
                                            onChangeMoney(setDailyRate)(
                                                e.target.value,
                                            )
                                        }
                                        disabled={!selectedStaffId}
                                        style={{ textAlign: "center" }}
                                    />
                                </div>
                                <div className="admin-input-container admin-input-comm">
                                    <div className="admin-staff-label">
                                        Min:
                                    </div>
                                    <input
                                        className="admin-staff-input"
                                        type="text"
                                        placeholder="฿"
                                        value={
                                            minimum?.toString().length > 0
                                                ? minimum
                                                : ""
                                        }
                                        onChange={e =>
                                            onChangeMoney(setMinimum)(
                                                e.target.value,
                                            )
                                        }
                                        disabled={!selectedStaffId}
                                        style={{ textAlign: "center" }}
                                    />
                                </div>
                                <div className="admin-input-container">
                                    <div className="admin-staff-label">
                                        Active:
                                    </div>
                                    <div className="active-radio-group">
                                        {activeOptions.map(
                                            ({ value, label }) => (
                                                <div key={label}>
                                                    <input
                                                        type="radio"
                                                        className="active-radio-input"
                                                        name="active-radio"
                                                        id={`active-${label}`}
                                                        checked={
                                                            active === value
                                                        }
                                                        onChange={() =>
                                                            onSelectActive(
                                                                value,
                                                            )
                                                        }
                                                        disabled={
                                                            !selectedStaffId
                                                        }
                                                    />
                                                    <label
                                                        className="active-radio-label"
                                                        htmlFor={`active-${label}`}
                                                    >
                                                        {label}
                                                    </label>
                                                </div>
                                            ),
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="admin-staff-row">
                                <div className="admin-input-container">
                                    <div className="admin-staff-label">
                                        Shop:
                                    </div>
                                    <div className="admin-shop-select">
                                        <Select
                                            options={shopOptions}
                                            value={shopOpt}
                                            onChange={opt => setShopOpt(opt)}
                                            placeholder="Select shop"
                                            styles={{
                                                control: baseStyles => ({
                                                    ...baseStyles,
                                                    boxShadow:
                                                        "0 2px 4px rgba(0,0,0, .2)",
                                                    border: "0",
                                                }),
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div
                                className="admin-staff-row"
                                style={{ marginTop: "5px" }}
                            >
                                <div className="admin-input-container">
                                    <button
                                        type="button"
                                        className="admin-reset-button"
                                        onClick={onClickReset}
                                    >
                                        Reset Password
                                    </button>
                                </div>
                                <div className="admin-input-container">
                                    <button
                                        type="button"
                                        className="admin-staff-button"
                                        disabled={!formIsValid}
                                        onClick={onClickSubmit}
                                        style={{
                                            opacity: formIsValid ? 1 : ".4",
                                        }}
                                    >
                                        Save Changes
                                    </button>
                                </div>
                            </div>
                            {selectedIsOrphan ? (
                                <div className="admin-orphan-row">
                                    <button
                                        type="button"
                                        className="admin-orphan-button"
                                        onClick={onClickDeleteOrphan}
                                    >
                                        Delete Orphan
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    ) : null}

                    {/* outside the block above, which only renders once
                        somebody is selected -- this is most wanted before that,
                        to find a staff member the active-only list is hiding.
                        With a selection it still sits directly under Reset
                        Password and Save Changes. */}
                    <div className="admin-show-all-row">
                        <button
                            type="button"
                            className="admin-show-all-button"
                            onClick={onToggleShowAll}
                        >
                            {showingAll ? "Active Only" : "Show All"}
                        </button>
                    </div>
                </div>
                <div className="admin-gutter-right" />
            </div>

            <div className="admin-column admin-column-narrow">
                <div
                    style={{
                        marginTop: "20px",
                        marginBottom: "9px",
                        fontSize: "22px",
                    }}
                >
                    Staff Payments
                </div>
                <div className="select-wrapper" style={{ borderRight: 0 }}>
                    <Select
                        value={selectedMonth}
                        onChange={opt => onSelectMonth(opt)}
                        options={monthOptions}
                    />
                </div>

                <div className="data-wrapper" style={{ borderRight: 0 }}>
                    {/* on the staff member alone. Gating on the payments
                        list meant a person who had never been paid could not
                        be paid -- the panel that records a payment was the
                        thing being hidden. */}
                    {selectedStaffId ? (
                        <Commissions
                            commissionTotals={commissionTotals}
                            selectedStaffId={selectedStaffId}
                            selectedStaffName={selectedStaffName}
                            selectedMonth={selectedMonth}
                            payments={payments || []}
                            /* Whether this person is paid at all, not
                               whether they earn commission. Gating on the
                               commission rate hid the payment form from
                               anyone on salary -- Soom's rate is 0 -- so
                               there was no way to record paying them. */
                            /* what the days-worked control needs: the count,
                               what the sales say it should be, and the rate
                               it is multiplied by */
                            days={commission.days?.[selectedStaffId]}
                            payable={
                                (shownStaffObj[selectedStaffId].commission ||
                                    0) > 0 ||
                                (shownStaffObj[selectedStaffId].dailyRate ||
                                    0) > 0 ||
                                (shownStaffObj[selectedStaffId].minimum || 0) >
                                    0
                            }
                        />
                    ) : (
                        <div style={{ marginTop: "15px" }}>
                            {selectedStaffId
                                ? "Nothing this month"
                                : "Please select staff"}
                        </div>
                    )}
                </div>

                <div
                    style={{
                        flex: "0 1 0px",
                        backgroundColor: "white",
                        padding: "0px",
                        borderRight: "0",
                    }}
                ></div>
            </div>
        </div>
    );
};

export default StaffAdmin;
